import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { BLOG_POSTS } from "@/data/blog";

export default function Blog() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Блог — Паруса на крыше | Гид по ресторанам и событиям Петербурга",
    description: "Статьи о ресторанах Петербурга, особых поводах, видах на город и лучших местах для ужина. Гид от ресторана Паруса на крыше.",
    keywords: "блог ресторан Петербург, гид рестораны СПб, особые поводы Петербург, где отметить день рождения СПб, рестораны с видом Петербург",
    canonical: "/blog",
  });
  useBreadcrumbs([{ name: "Блог", path: "/blog" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="py-32 border-b border-primary/10 relative overflow-hidden">
        <div className="absolute right-0 top-0 font-serif text-[380px] font-light text-primary/[0.03] leading-none select-none pointer-events-none translate-x-20 -translate-y-10">Б</div>
        <div className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(20px)", transition: "all 0.8s ease 0.2s" }}>
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Журнал</div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-tight max-w-3xl">
            Гид по Петербургу<br /><span className="text-accent italic">с высоты крыши</span>
          </h1>
          <p className="text-muted-foreground font-light text-lg max-w-xl leading-relaxed">
            Пишем о лучших местах города, особых поводах и о том, как сделать вечер незабываемым.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          {/* Featured */}
          <a
            href={`/blog/${BLOG_POSTS[0].slug}`}
            className="group block border border-primary/20 hover:border-primary/50 transition-colors mb-8 overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative overflow-hidden aspect-[16/9] lg:aspect-auto">
                <img src={BLOG_POSTS[0].image} alt={BLOG_POSTS[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ minHeight: "300px" }} loading="eager" />
                <div className="absolute inset-0 bg-background/30" />
                <div className="absolute top-6 left-6">
                  <span className="bg-primary/20 border border-primary/40 text-accent text-[9px] tracking-[0.3em] uppercase px-4 py-2">{BLOG_POSTS[0].category}</span>
                </div>
              </div>
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <div className="text-[9px] tracking-[0.3em] text-primary/40 uppercase mb-4">{formatDate(BLOG_POSTS[0].date)} · {BLOG_POSTS[0].readTime} мин</div>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-5 leading-tight group-hover:text-accent transition-colors">{BLOG_POSTS[0].title}</h2>
                <p className="text-muted-foreground text-sm font-light leading-relaxed mb-8 line-clamp-3">{BLOG_POSTS[0].lead}</p>
                <span className="text-[10px] tracking-[0.3em] uppercase text-primary group-hover:text-accent transition-colors">Читать статью →</span>
              </div>
            </div>
          </a>

          {/* Other posts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(1).map((post) => (
              <a
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="group block border border-primary/15 hover:border-primary/50 transition-colors overflow-hidden"
              >
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-background/40" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-background/80 text-accent text-[8px] tracking-[0.25em] uppercase px-3 py-1.5">{post.category}</span>
                  </div>
                </div>
                <div className="p-7">
                  <div className="text-[9px] tracking-[0.3em] text-primary/40 uppercase mb-3">{formatDate(post.date)} · {post.readTime} мин</div>
                  <h2 className="font-serif text-xl font-light text-foreground mb-3 leading-snug group-hover:text-accent transition-colors line-clamp-2">{post.title}</h2>
                  <p className="text-muted-foreground text-xs font-light leading-relaxed line-clamp-3 mb-5">{post.lead}</p>
                  <span className="text-[9px] tracking-[0.3em] uppercase text-primary/60 group-hover:text-accent transition-colors">Читать →</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
